import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Switch } from '@/components/ui/Switch';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { cn } from '@/lib/utils';
import { toast } from '@/store/toastStore';
import { fieldErrorsFromError } from './autoresponders/autoresponderForm';
import * as adminSvc from '@/services/admin.service';
import type {
  NewPatientFormAdminDocument,
  NewPatientFormAdminInput,
  NewPatientFormDefaultOption,
  NewPatientFormFieldInput,
  NewPatientFormOptionHint,
  NewPatientFormOptionHintInput,
  NewPatientFormOptionHints,
} from '@/services/admin.service';
import {
  CARD_ORDER,
  type FieldCard,
  type FieldModeState,
  type FormMode,
  type OptionHintList,
  type ResolvedField,
} from '@/pages/leads/newPatientForm/newPatientFormRules';

// ─── Constants ───────────────────────────────────────────────────────────────

const MODES: { mode: FormMode; title: string }[] = [
  { mode: 'new', title: 'New patient' },
  { mode: 'convert_lead', title: 'Convert lead' },
  { mode: 'add_plan', title: 'Add plan' },
];

const CARD_TITLES: Record<FieldCard, string> = {
  patient: 'Patient Information',
  contact: 'Contact Information',
  plan: 'Plan Setup',
  interests: 'Interests',
  notes: 'Notes',
};

const OPTION_LISTS: { list: OptionHintList; title: string }[] = [
  { list: 'doctors', title: 'Doctors' },
  { list: 'facilities', title: 'Facilities' },
  { list: 'languages', title: 'Languages' },
  { list: 'procedures_subcategories', title: 'Procedures' },
  { list: 'referral_level1', title: 'Lead source categories' },
  { list: 'referral_level2', title: 'Lead sources' },
];

/** Gap left between renumbered orders inside a card. */
const ORDER_STEP = 10;

const PARTIAL_FAILURE =
  'Field settings were saved, but dropdown choice hints were not. Try saving again.';

// ─── Draft model ─────────────────────────────────────────────────────────────

/** One field as the page edits it. Strings are empty when unset. */
interface DraftField {
  key: string;
  order: number;
  label: string;
  hint: string;
  default: string;
  modes: Record<FormMode, FieldModeState>;
}

function draftFrom(fields: ResolvedField[]): DraftField[] {
  return fields.map((f) => ({
    key: f.key,
    order: f.order,
    label: f.customLabel ?? '',
    hint: f.hint ?? '',
    default: f.default ?? '',
    modes: {
      new: { ...f.modes.new },
      convert_lead: { ...f.modes.convert_lead },
      add_plan: { ...f.modes.add_plan },
    },
  }));
}

function payloadFrom(hintsEnabled: boolean, draft: DraftField[]): NewPatientFormAdminInput {
  return {
    hintsEnabled,
    fields: draft.map(
      (d): NewPatientFormFieldInput => ({
        key: d.key,
        order: d.order,
        label: d.label.trim(),
        hint: d.hint.trim(),
        default: d.default,
        modes: d.modes,
      }),
    ),
  };
}

function hintKey(list: OptionHintList, id: number): string {
  return `${list}:${id}`;
}

/**
 * The server's sentence when it answered, else our own. A request that never
 * got an answer carries only a transport message ("Network Error"), which is
 * not something a user can act on.
 */
function messageFrom(err: unknown, fallback: string): string {
  return axios.isAxiosError(err) && err.response ? getErrorMessage(err, fallback) : fallback;
}

// ─── Field row ───────────────────────────────────────────────────────────────

interface FieldRowProps {
  field: ResolvedField;
  draft: DraftField;
  defaultOptions: NewPatientFormDefaultOption[];
  isFirst: boolean;
  isLast: boolean;
  expanded: boolean;
  error: string | undefined;
  /** True while a save is in flight; the response replaces the draft. */
  disabled: boolean;
  /** Locked page level: no reordering, and the default reads as text. */
  locked: boolean;
  onToggleExpanded: () => void;
  onModeChange: (mode: FormMode, next: FieldModeState) => void;
  onChange: (patch: Partial<Pick<DraftField, 'label' | 'hint' | 'default'>>) => void;
  onMove: (direction: 'up' | 'down') => void;
  registerMoveButton: (direction: 'up' | 'down', el: HTMLButtonElement | null) => void;
}

function FieldRow({
  field,
  draft,
  defaultOptions,
  isFirst,
  isLast,
  expanded,
  error,
  disabled,
  locked,
  onToggleExpanded,
  onModeChange,
  onChange,
  onMove,
  registerMoveButton,
}: FieldRowProps) {
  const name = draft.label.trim() || field.caption;
  const idBase = `npf-${field.key}`;
  const defaultKnown = draft.default === '' || defaultOptions.some((o) => o.value === draft.default);
  const defaultLabel =
    draft.default === ''
      ? 'No default'
      : (defaultOptions.find((o) => o.value === draft.default)?.label ?? 'No longer available');

  return (
    <>
      <tr
        data-field-key={field.key}
        aria-invalid={error ? true : undefined}
        className={cn('block md:table-row', error ? 'bg-red-50' : 'hover:bg-slate-50')}
      >
        <td className="block md:table-cell px-3 pt-2.5 md:py-2.5 align-top">
          <div className="flex items-start gap-1.5">
            <button
              type="button"
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              aria-controls={`${idBase}-details`}
              aria-label={`Edit label, hint and default for ${name}`}
              className="mt-0.5 p-0.5 text-slate-400 hover:text-slate-600"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            <div className="min-w-0">
              <span className="font-medium text-slate-900">{name}</span>
              {name !== field.caption && (
                <span className="ml-1.5 text-xs text-slate-500">({field.caption})</span>
              )}
              {field.locked && (
                <p className="text-xs text-slate-500">Always shown and required.</p>
              )}
              {error && (
                <p role="alert" className="text-xs text-red-600">
                  {error}
                </p>
              )}
            </div>
          </div>
        </td>

        {MODES.map(({ mode, title }) => {
          const allowed = field.allowedModes.indexOf(mode) !== -1;
          const state = draft.modes[mode];
          return (
            <td
              key={mode}
              data-mode={mode}
              className="block md:table-cell px-3 py-1 md:py-2.5 align-top"
            >
              <div className="flex items-center gap-3 md:justify-center">
                <span className="md:hidden w-24 text-xs text-slate-500">{title}</span>
                {allowed ? (
                  <>
                    <span className="flex items-center gap-1.5 md:w-16 md:justify-center">
                      <span className="md:hidden text-xs text-slate-500">Shown</span>
                      <Switch
                        checked={state.shown}
                        disabled={disabled || field.locked}
                        aria-label={`${name}, shown in ${title.toLowerCase()}`}
                        onChange={(shown) =>
                          onModeChange(mode, { shown, required: shown ? state.required : false })
                        }
                      />
                    </span>
                    <span className="flex items-center gap-1.5 md:w-16 md:justify-center">
                      {field.hasRequired && (
                        <>
                          <span className="md:hidden text-xs text-slate-500">Required</span>
                          <Switch
                            checked={state.shown && state.required}
                            disabled={disabled || field.locked || !state.shown}
                            aria-label={`${name}, required in ${title.toLowerCase()}`}
                            onChange={(required) => onModeChange(mode, { shown: state.shown, required })}
                          />
                        </>
                      )}
                    </span>
                  </>
                ) : (
                  <span className="text-slate-400 md:w-32 md:text-center">
                    <span aria-hidden="true">—</span>
                    <span className="sr-only">{`Not asked in ${title.toLowerCase()}`}</span>
                  </span>
                )}
              </div>
            </td>
          );
        })}

        {!locked && (
          <td className="block md:table-cell px-3 pb-2.5 md:py-2.5 align-top">
            <div className="flex items-center gap-1 md:justify-end">
              <button
                type="button"
                ref={(el) => registerMoveButton('up', el)}
                onClick={() => onMove('up')}
                disabled={disabled || isFirst}
                className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Move ${name} up`}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                ref={(el) => registerMoveButton('down', el)}
                onClick={() => onMove('down')}
                disabled={disabled || isLast}
                className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Move ${name} down`}
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </td>
        )}
      </tr>

      {expanded && (
        <tr id={`${idBase}-details`} className={cn('block md:table-row', error ? 'bg-red-50' : 'bg-slate-50')}>
          <td colSpan={(locked ? 1 : 2) + MODES.length} className="block md:table-cell px-3 py-3">
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="flex-1 min-w-0">
                <FormField label="Label" htmlFor={`${idBase}-label`}>
                  <Input
                    id={`${idBase}-label`}
                    aria-label={`Label for ${field.caption}`}
                    value={draft.label}
                    onChange={(e) => onChange({ label: e.target.value })}
                    disabled={disabled}
                    placeholder={field.caption}
                    maxLength={100}
                  />
                </FormField>
              </div>
              <div className="flex-1 min-w-0">
                <FormField label="Hint" htmlFor={`${idBase}-hint`}>
                  <Input
                    id={`${idBase}-hint`}
                    aria-label={`Hint for ${field.caption}`}
                    value={draft.hint}
                    onChange={(e) => onChange({ hint: e.target.value })}
                    disabled={disabled}
                  />
                </FormField>
              </div>
              {field.acceptsDefault && (
                <div className="flex-1 min-w-0">
                  <FormField label="Default" htmlFor={`${idBase}-default`}>
                    {locked ? (
                      <Input
                        id={`${idBase}-default`}
                        aria-label={`Default for ${field.caption}`}
                        value={defaultLabel}
                        readOnly
                      />
                    ) : (
                      <select
                        id={`${idBase}-default`}
                        aria-label={`Default for ${field.caption}`}
                        value={draft.default}
                        onChange={(e) => onChange({ default: e.target.value })}
                        disabled={disabled}
                        className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900"
                      >
                        <option value="">No default</option>
                        {defaultOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                        {!defaultKnown && <option value={draft.default}>No longer available</option>}
                      </select>
                    )}
                  </FormField>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Option hints ────────────────────────────────────────────────────────────

interface OptionListProps {
  title: string;
  list: OptionHintList;
  options: NewPatientFormOptionHint[];
  parentNames: Map<number, string>;
  edits: Record<string, string>;
  errors: Record<string, string>;
  disabled: boolean;
  onEdit: (id: number, hint: string) => void;
}

function OptionList({ title, list, options, parentNames, edits, errors, disabled, onEdit }: OptionListProps) {
  const hasError = options.some((o) => errors[hintKey(list, o.id)] !== undefined);
  const [open, setOpen] = useState(false);
  const expanded = open || hasError;

  return (
    <div className="border border-slate-200 rounded-lg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-slate-900 hover:bg-slate-50"
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        {title}
        <span className="text-xs font-normal text-slate-500">{options.length}</span>
      </button>
      {expanded && (
        <div className="border-t border-slate-200 divide-y divide-slate-100">
          {options.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-500">None yet</p>
          ) : (
            options.map((o) => {
              const key = hintKey(list, o.id);
              const error = errors[key];
              const parent = o.parentId !== null ? parentNames.get(o.parentId) : undefined;
              return (
                <div
                  key={o.id}
                  className={cn('flex flex-col gap-1 px-3 py-2 md:flex-row md:items-start md:gap-3', error && 'bg-red-50')}
                >
                  <div className="md:w-1/3 min-w-0 pt-1.5 text-sm text-slate-900">
                    {o.name}
                    {parent && <span className="ml-1.5 text-xs text-slate-500">{parent}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Input
                      aria-label={`Hint for ${o.name}`}
                      value={edits[key] ?? o.hint}
                      onChange={(e) => onEdit(o.id, e.target.value)}
                      disabled={disabled}
                      error={error}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function NewLeadFormAdmin() {
  // Locked: a viewer — every setting read-only, no reordering, no Save.
  const locked = useAdminPageLevel() === 'locked';
  const [doc, setDoc] = useState<NewPatientFormAdminDocument | null>(null);
  const [optionHints, setOptionHints] = useState<NewPatientFormOptionHints | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [draft, setDraft] = useState<DraftField[]>([]);
  const [hintsEnabled, setHintsEnabled] = useState(false);
  const [hintEdits, setHintEdits] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hintErrors, setHintErrors] = useState<Record<string, string>>({});

  const moveButtons = useRef(new Map<string, HTMLButtonElement>());
  const [pendingFocus, setPendingFocus] = useState<{ key: string; direction: 'up' | 'down' } | null>(null);

  const applyDocument = useCallback((next: NewPatientFormAdminDocument) => {
    setDoc(next);
    setDraft(draftFrom(next.fields));
    setHintsEnabled(next.hintsEnabled);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, hints] = await Promise.all([
        adminSvc.getNewPatientFormAdmin(),
        adminSvc.getNewPatientFormOptionHints(),
      ]);
      applyDocument(settings);
      setOptionHints(hints);
      setLoadError(null);
    } catch (err) {
      setLoadError(messageFrom(err, "Form settings couldn't be loaded. Refresh the page to try again."));
    } finally {
      setLoading(false);
    }
  }, [applyDocument]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Dirty tracking ───────────────────────────────────────────────────

  const savedPayload = useMemo(
    () => (doc ? JSON.stringify(payloadFrom(doc.hintsEnabled, draftFrom(doc.fields))) : ''),
    [doc],
  );
  const settingsDirty = doc !== null && JSON.stringify(payloadFrom(hintsEnabled, draft)) !== savedPayload;

  const changedHints = useMemo((): NewPatientFormOptionHintInput[] => {
    if (!optionHints) return [];
    const out: NewPatientFormOptionHintInput[] = [];
    for (const { list } of OPTION_LISTS) {
      for (const o of optionHints[list] ?? []) {
        const edit = hintEdits[hintKey(list, o.id)];
        if (edit !== undefined && edit !== o.hint) out.push({ list, id: o.id, hint: edit });
      }
    }
    return out;
  }, [optionHints, hintEdits]);

  const dirty = settingsDirty || changedHints.length > 0;
  useUnsavedChangesGuard(dirty);

  // ── Editing ──────────────────────────────────────────────────────────

  function patchField(key: string, patch: Partial<DraftField>) {
    setDraft((list) => list.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }

  function setMode(key: string, mode: FormMode, next: FieldModeState) {
    setDraft((list) =>
      list.map((d) => (d.key === key ? { ...d, modes: { ...d.modes, [mode]: next } } : d)),
    );
  }

  const fieldsByKey = useMemo(() => {
    const map = new Map<string, ResolvedField>();
    for (const f of doc?.fields ?? []) map.set(f.key, f);
    return map;
  }, [doc]);

  /** Card rows in their current order; only cards that have fields. */
  const cards = useMemo(() => {
    return CARD_ORDER.map((card) => ({
      card,
      rows: draft
        .filter((d) => fieldsByKey.get(d.key)?.card === card)
        .sort((a, b) => a.order - b.order),
    })).filter((c) => c.rows.length > 0);
  }, [draft, fieldsByKey]);

  /** Swap with the neighbour and renumber that card only. */
  function move(key: string, direction: 'up' | 'down') {
    const card = cards.find((c) => c.rows.some((r) => r.key === key));
    if (!card) return;
    const keys = card.rows.map((r) => r.key);
    const from = keys.indexOf(key);
    const to = direction === 'up' ? from - 1 : from + 1;
    if (to < 0 || to >= keys.length) return;
    keys[from] = keys[to];
    keys[to] = key;
    const orders = new Map(keys.map((k, i) => [k, (i + 1) * ORDER_STEP]));
    setDraft((list) => list.map((d) => (orders.has(d.key) ? { ...d, order: orders.get(d.key) ?? d.order } : d)));
    setPendingFocus({ key, direction });
  }

  // Keep focus on the moved row; at the end of its card, hand it to the other arrow.
  useEffect(() => {
    if (!pendingFocus) return;
    const { key, direction } = pendingFocus;
    const same = moveButtons.current.get(`${key}:${direction}`);
    const other = moveButtons.current.get(`${key}:${direction === 'up' ? 'down' : 'up'}`);
    (same && !same.disabled ? same : other)?.focus();
    setPendingFocus(null);
  }, [pendingFocus, draft]);

  // ── Save ─────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    setHintErrors({});

    let settingsSaved = false;
    if (settingsDirty) {
      try {
        applyDocument(await adminSvc.updateNewPatientFormAdmin(payloadFrom(hintsEnabled, draft)));
        settingsSaved = true;
      } catch (err) {
        const errors = fieldErrorsFromError(err);
        setFieldErrors(errors);
        setExpanded((e) => {
          const next = { ...e };
          for (const key of Object.keys(errors)) if (fieldsByKey.has(key)) next[key] = true;
          return next;
        });
        setSaveError(messageFrom(err, 'Your changes were not saved. Try again.'));
        setSaving(false);
        return;
      }
    }

    const sent = changedHints;
    if (sent.length > 0) {
      try {
        setOptionHints(await adminSvc.updateNewPatientFormOptionHints(sent));
        setHintEdits({});
      } catch (err) {
        const byIndex = fieldErrorsFromError(err);
        const errors: Record<string, string> = {};
        for (const [index, message] of Object.entries(byIndex)) {
          const entry = sent[Number(index)];
          if (entry) errors[hintKey(entry.list, entry.id)] = message;
        }
        setHintErrors(errors);
        setSaveError(
          settingsSaved ? PARTIAL_FAILURE : messageFrom(err, 'Your hint changes were not saved. Try again.'),
        );
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    toast.success('New Patient form saved');
  }

  // ── Render ───────────────────────────────────────────────────────────

  const parentNames = useMemo(
    () => new Map((optionHints?.referral_level1 ?? []).map((o) => [o.id, o.name])),
    [optionHints],
  );

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader
          title="New Patient Form"
          subtitle="Choose which details the New Patient form asks for."
          actions={
            locked ? undefined : (
              <Button onClick={() => void handleSave()} disabled={!dirty || saving} loading={saving}>
                Save
              </Button>
            )
          }
        />

        {saveError && (
          <p role="alert" className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {saveError}
          </p>
        )}
      </div>

      {loadError ? (
        <p className="flex-shrink-0 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {loadError}
        </p>
      ) : loading || !doc ? (
        <SpinnerOverlay label="Loading form settings…" />
      ) : (
        <AdminLockedFieldset>
          {/* relative: the rows' sr-only spans are absolutely positioned, and
              without a positioned scroller they size the page's <main>,
              which then scrolls as well. */}
          <div className="relative flex-1 min-h-0 overflow-y-auto space-y-6">
            <div className="flex items-center gap-2">
              <Switch
                id="npf-hints-enabled"
                checked={hintsEnabled}
                onChange={setHintsEnabled}
                disabled={saving}
                aria-label="Show hints on the form"
              />
              <label htmlFor="npf-hints-enabled" className="text-sm text-slate-700 cursor-pointer select-none">
                Show hints on the form
              </label>
            </div>

            {cards.map(({ card, rows }) => (
              <section key={card} data-card={card} aria-labelledby={`npf-card-${card}`}>
                <h2 id={`npf-card-${card}`} className="mb-2 text-sm font-semibold text-slate-900">
                  {CARD_TITLES[card]}
                </h2>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="block md:table w-full">
                    <thead className="hidden md:table-header-group">
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2">
                          Field
                        </th>
                        {MODES.map(({ mode, title }) => (
                          <th
                            key={mode}
                            className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2"
                          >
                            <div>{title}</div>
                            <div className="flex justify-center font-normal normal-case tracking-normal">
                              <span className="w-16">Shown</span>
                              <span className="w-16">Required</span>
                            </div>
                          </th>
                        ))}
                        {!locked && (
                          <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2 w-20">
                            Order
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="block md:table-row-group divide-y divide-slate-200">
                      {rows.map((d, i) => {
                        const f = fieldsByKey.get(d.key);
                        if (!f) return null;
                        return (
                          <FieldRow
                            key={d.key}
                            field={f}
                            draft={d}
                            defaultOptions={doc.defaultOptions[d.key] ?? []}
                            isFirst={i === 0}
                            isLast={i === rows.length - 1}
                            expanded={expanded[d.key] === true}
                            error={fieldErrors[d.key]}
                            disabled={saving}
                            locked={locked}
                            onToggleExpanded={() => setExpanded((e) => ({ ...e, [d.key]: !e[d.key] }))}
                            onModeChange={(mode, next) => setMode(d.key, mode, next)}
                            onChange={(patch) => patchField(d.key, patch)}
                            onMove={(direction) => move(d.key, direction)}
                            registerMoveButton={(direction, el) => {
                              const k = `${d.key}:${direction}`;
                              if (el) moveButtons.current.set(k, el);
                              else moveButtons.current.delete(k);
                            }}
                          />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}

            {optionHints && (
              <section aria-labelledby="npf-option-hints">
                <h2 id="npf-option-hints" className="mb-2 text-sm font-semibold text-slate-900">
                  Dropdown choice hints
                </h2>
                <div className="space-y-2">
                  {OPTION_LISTS.map(({ list, title }) => (
                    <OptionList
                      key={list}
                      title={title}
                      list={list}
                      options={(optionHints[list] ?? []).filter((o) => o.active)}
                      parentNames={list === 'referral_level2' ? parentNames : new Map<number, string>()}
                      edits={hintEdits}
                      errors={hintErrors}
                      disabled={saving}
                      onEdit={(id, hint) => setHintEdits((e) => ({ ...e, [hintKey(list, id)]: hint }))}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </AdminLockedFieldset>
      )}
    </div>
  );
}
